import { useEffect, useState } from "react";
import { Store } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { ax } from "../../lib/adminApi";

const inputCls =
  "h-12 w-full rounded-xl border border-forest/15 bg-mist px-4 text-sm text-forest outline-none placeholder:text-forest/40 focus:ring-2 focus:ring-fresh";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-forest/50">{label}</span>
    {children}
  </label>
);

export const StoreLinksDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ app_store_url: "", play_store_url: "", launch_date: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    ax.get("/public-config")
      .then((r) =>
        setForm({
          app_store_url: r.data.app_store_url || "",
          play_store_url: r.data.play_store_url || "",
          launch_date: (r.data.launch_date || "").slice(0, 16),
        })
      )
      .catch(() => {});
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await ax.patch("/admin/settings", form);
      toast.success("Saved — the landing page uses the new links immediately.");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save. Check the URLs and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="store-links-dialog">
        <DialogHeader>
          <DialogTitle className="font-syne text-forest">Store links & launch moment</DialogTitle>
          <DialogDescription>
            Paste the real listings on launch day — visitors see them instantly, no redeploy needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="App Store URL">
            <input
              value={form.app_store_url}
              onChange={set("app_store_url")}
              placeholder="https://apps.apple.com/ca/app/freshly/id…"
              data-testid="settings-app-store-url"
              className={inputCls}
            />
          </Field>
          <Field label="Google Play URL">
            <input
              value={form.play_store_url}
              onChange={set("play_store_url")}
              placeholder="https://play.google.com/store/apps/details?id=…"
              data-testid="settings-play-store-url"
              className={inputCls}
            />
          </Field>
          <Field label="Launch date & time (countdown flips to badges when it passes)">
            <input
              type="datetime-local"
              value={form.launch_date}
              onChange={set("launch_date")}
              data-testid="settings-launch-date"
              className={inputCls}
            />
          </Field>
        </div>
        <DialogFooter>
          <button
            type="button"
            data-testid="settings-cancel"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-11 items-center rounded-full border border-forest/15 px-5 text-sm font-bold text-forest transition-colors hover:border-forest/40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            data-testid="settings-save"
            onClick={save}
            className="inline-flex h-11 items-center gap-2 rounded-full bg-fresh px-5 text-sm font-bold text-white transition-colors hover:bg-forest disabled:opacity-60"
          >
            <Store className="h-4 w-4" />
            {saving ? "Saving…" : "Save links"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
